<template>
    <main class="content">
        <header class="content-header">
            <section class="content-header-section">
                <router-link :to="`/${ $options.module }`" class="button button-back">
                    {{ $t('general.form.buttons.cancel') }}
                </router-link>
            </section>
            <section class="content-header-section">
                <button
                    type="button"
                    class="button button-gray"
                    @click="actionSave"
                >
                    {{ $t('general.form.buttons.save') }}
                </button>
                <button
                    type="button"
                    class="button button-blue"
                    @click="actionPublish"
                >
                    {{ $t('general.form.buttons.publish') }}
                </button>
            </section>
        </header>
        <div v-if="element" class="content-form">
            <div class="form-input">
                <div class="form-input-header">
                    <div class="label">{{ $t(`pages.${ this.$options.module }.form.labels.tags`) }}</div>
                </div>
                <div class="form-input-tags">
                    <vue-tags-input
                        v-model="tag"
                        :tags="tags"
                        @tags-changed="tagsChanged"
                    />
                </div>
            </div>
            <div :class="{ 'infopages-input-with-buttons': element.items.length === 0 }">
                <input-i18n
                    field="title"
                    :label="$t(`pages.${ this.$options.module }.form.labels.title`)"
                    :placeholder="$t(`pages.${ this.$options.module }.form.placeholders.title`)"
                    :locales="element.locales"
                    :errors="errors"
                    :currentLanguage="currentLanguage"
                    @actionChangeLang="actionChangeLang"
                    :maxlength="100"
                />
                <div class="form-item-actions" v-if="element.items.length === 0" ref="mainMenu">
                    <div class="action action-add" @click="mainMenuOpen = !mainMenuOpen">
                        <ul class="action-menu" v-if="mainMenuOpen">
                            <li @click="itemAdd(-1, 'subtitle-text')">Add Subtitle & text</li>
                            <li @click="itemAdd(-1, 'text')">Add Text</li>
                        </ul>
                    </div>
                </div>
            </div>
            <div class="form-items">
                <draggable
                    v-model="element.items"
                    @start="drag = true"
                    @end="drag = false"
                    handle=".form-item-drag-handler"
                    ghost-class="ghost"
                >
                    <div
                        v-for="(item, itemIndex) in element.items"
                        :key="itemIndex"
                        class="form-item"
                    >
                        <div class="form-item-drag-handler"/>
                        <div class="form-item-content">
                            <div class="form-input-header">
                                <div class="label">{{ $t(`pages.${ $options.module }.form.labels.${ item.type }`) }}</div>
                                <div class="languages">
                                    <span
                                        v-for="lang in $options.languages"
                                        :key="lang"
                                        :class="{ 'lang-button': true, active: currentLanguage === lang }"
                                        @click="actionChangeLang(lang)"
                                    >
                                        {{ lang }}
                                    </span>
                                </div>
                            </div>
                            <div v-if="item.type === 'subtitle-text'" class="form-input-field">
                                <input
                                    v-model="item.locales[currentLanguage].title"
                                    type="text"
                                    v-bind:placeholder="$t(`pages.${ $options.module }.form.placeholders.subtitle`)"
                                />
                            </div>
                            <div class="form-input-field">
                                <input-editor
                                    field="text"
                                    :element="item.locales[currentLanguage]"
                                    :errors="null"
                                />
                            </div>
                        </div>
                        <div class="form-item-actions">
                            <span
                                v-on:click="itemRemove(itemIndex)"
                                class="action action-remove"
                            />
                            <span
                                v-if="item.type === 'subtitle-text'"
                                v-on:click="itemAdd(itemIndex, 'subtitle-text')"
                                class="action action-add"
                            />
                        </div>
                    </div>
                </draggable>
            </div>
        </div>
        <pop-down
            v-if="pop.active"
            :class="pop.className"
            @close="popClose"
        >
            {{ pop.message }}
        </pop-down>
    </main>
</template>

<script>
import VueTagsInput from '@johmun/vue-tags-input';
import draggable from 'vuedraggable';
import merge from 'lodash/merge';

import PopDownMixin from '@/components/PopDownMixin';
import FormMixin from '@/components/form/FormMixin';

export default {
    mixins: [FormMixin, PopDownMixin],
    name: 'InfoPagesForm',
    module: 'infopages',
    components: {
        VueTagsInput,
        draggable,
    },
    emptyElement: {
        state: 0,
        locales: {
            de: {
                title: '',
            },
            it: {
                title: '',
            },
            fr: {
                title: '',
            },
            en: {
                title: '',
            },
        },
        items: [],
        tags: [],
    },
    emptyChild: {
        type: 'text', // subtitle-text
        locales: {
            de: {
                title: '',
                text: '',
            },
            it: {
                title: '',
                text: '',
            },
            fr: {
                title: '',
                text: '',
            },
            en: {
                title: '',
                text: '',
            },
        },
    },
    data() {
        return {
            mainMenuOpen: false,
            tag: '',
            tags: [],
        };
    },
    mounted() {
        document.body.addEventListener('click', this.clickOutsideMainMenu);
    },
    beforeDestroy() {
        document.body.removeEventListener('click', this.clickOutsideMainMenu);
    },
    methods: {
        initData() {
            if (this.$route.name === `${ this.$options.module }.add`) {
                this.type = 'add';
                this.element = {
                    ...JSON.parse(JSON.stringify(this.$options.emptyElement)),
                };
                this.tags = [];
            } else if (this.$route.name === `${ this.$options.module }.edit`) {
                const element = this.editElement;
                if (element === null) {
                    this.actionBackToList('error_nonexistent');
                    return;
                }
                this.type = 'edit';
                this.element = merge(
                    {},
                    this.$options.emptyElement,
                    element,
                );
                this.tags = this.element.tags === '' ? [] : this.element.tags.split(',').map((t) => ({
                    text: t,
                }));
            } else {
                this.actionBackToList('error_router');
            }
        },
        itemAdd(prevIndex, type) {
            const item = merge(
                { },
                this.$options.emptyChild,
                { type },
            );
            this.element.items.splice(prevIndex + 1, 0, item);
        },
        itemRemove(index) {
            this.element.items.splice(index, 1);
        },
        tagsChanged(newTags) {
            this.tags = newTags;
            this.element.tags = newTags.map(({ text }) => text).join(',');
        },
        clickOutsideMainMenu(event) {
            if (this.mainMenuOpen) {
                if (!(this.$refs.mainMenu === event.target || this.$refs.mainMenu.contains(event.target))) {
                    this.mainMenuOpen = false;
                }
            }
        },
    },
};
</script>
